import type { ReactNode } from 'react';
import type { PageContext } from 'vike/types';

import { en } from '@bitwize/i18n-messages';

import { I18nProvider, pathnameOf } from '../shared/i18n';
import { PageContextProvider } from '../shared/lib';
import { ThemeProvider } from '../shared/theme';
import { RootLayout } from './RootLayout';

export interface ProvidersProps {
  pageContext: PageContext;
  children: ReactNode;
}

/**
 * Everything that wraps every page, on both the server and the client.
 *
 * Kept in one component so the two renderers cannot drift: a provider present
 * during hydration but absent during server rendering produces a mismatch that
 * React repairs silently and expensively.
 *
 * The English catalogue is the fallback for a UI string a translator has not
 * reached yet. It is a message-catalogue fallback only — database content never
 * falls back here, because that policy is a product decision (Q-08) applied by
 * the renderer using the API's `missing_locales`.
 */
export function Providers({ pageContext, children }: ProvidersProps) {
  // `urlPathname` is server-only — Vike's client `pageContext` carries
  // `urlOriginal` instead (see `useCurrentUrl`'s doc comment), which is what
  // RootLayout's own admin/public split already derives its pathname from.
  const isAdmin = pathnameOf(pageContext.urlOriginal).startsWith('/admin');

  return (
    <PageContextProvider value={pageContext}>
      <ThemeProvider enabled={isAdmin}>
        <I18nProvider
          locale={pageContext.locale}
          messages={pageContext.messages}
          fallbackMessages={en}
        >
          <RootLayout>{children}</RootLayout>
        </I18nProvider>
      </ThemeProvider>
    </PageContextProvider>
  );
}
